import { differenceInMinutes, addMinutes } from 'date-fns';
import { deliveryGracePeriodInMinutes } from '@sporkbytes/config';
import { getClientApproved } from '@sporkbytes/meal-option-utils';
import { getTimeOfDay } from '@sporkbytes/time-utils';

const CLOSE_PROXIMITY_DISTANCE = 50;
const BASE_HEAD_COUNT = 30;
const DELIVERY_GRACE_PERIOD = deliveryGracePeriodInMinutes;
const ADDITIONAL_TIME_SPENT_PICKING_UP_PER_PERSON_ABOVE_BASE_HEAD_COUNT = 0.1;
const ADDITIONAL_TIME_SPENT_SETTING_UP_PER_PERSON_ABOVE_BASE_HEAD_COUNT = 0.1;
const MIN_TIME_SPENT_PICKING_UP = 8;
const MAX_TIME_SPENT_PICKING_UP = 15;
const MIN_TIME_SPENT_SETTING_UP = 10;
const MAX_TIME_SPENT_SETTING_UP = 20;
function addReferencesToDuplicateDestinationPoints(route) {
  route.forEach((destinationPoint, index) => {
    destinationPoint.duplicates = [];
    const {
      id
    } = destinationPoint;
    function iterateThoughRouteAndFindDuplicates(startingIndex, condition, newIndex) {
      for (let i = startingIndex; condition(i); i = newIndex(i)) {
        const adjacentDestinationPoint = route[i];
        if (adjacentDestinationPoint.id === id) {
          destinationPoint.duplicates.push({
            index: i,
            destinationPoint: adjacentDestinationPoint
          });
        } else {
          break;
        }
      }
    }
    iterateThoughRouteAndFindDuplicates(index - 1, i => i >= 0, i => --i);
    iterateThoughRouteAndFindDuplicates(index + 1, i => i < route.length, i => ++i);
  });
  return route;
}
function calculateTimes(route, distances) {
  let firstPoint = route[0];
  firstPoint.arrivalTime = addMinutes(firstPoint.jobStartTimeRange.earliest, -firstPoint.timeToEnter);
  firstPoint.startedJobTime = firstPoint.jobStartTimeRange.earliest;
  firstPoint.earlyBy = 0;
  firstPoint.lateBy = 0;
  firstPoint.finishedJobTime = addMinutes(firstPoint.startedJobTime, firstPoint.timeSpentDoingJob);
  if (route.length > 1 && firstPoint.id === route[1].id) {
    firstPoint.timeToExit = 0;
  }
  firstPoint.leaveTime = addMinutes(firstPoint.finishedJobTime, firstPoint.timeToExit);
  for (let index = 1; index < route.length; index++) {
    let fromPoint = route[index - 1];
    let toPoint = route[index];
    const {
      minutesToLocation,
      meters
    } = distances[fromPoint.id][toPoint.id][fromPoint.isWeekday][fromPoint.timeOfDay];
    toPoint.minutesToLocation = minutesToLocation;
    toPoint.metersToLocation = meters;
    toPoint.timeToPark = toPoint.metersToLocation < CLOSE_PROXIMITY_DISTANCE ? 0 : toPoint.timeToPark;
    toPoint.timeToEnter = fromPoint.id === toPoint.id ? 0 : toPoint.timeToEnter;
    toPoint.arrivalTime = addMinutes(fromPoint.leaveTime, toPoint.minutesToLocation + toPoint.timeToPark);
    toPoint.readyToStartJobTime = addMinutes(toPoint.arrivalTime, toPoint.timeToEnter);
    toPoint.earlyBy = differenceInMinutes(toPoint.jobStartTimeRange.earliest, toPoint.readyToStartJobTime);
    toPoint.startedJobTime = toPoint.earlyBy > 0 ? toPoint.jobStartTimeRange.earliest : toPoint.readyToStartJobTime;
    toPoint.lateBy = differenceInMinutes(toPoint.startedJobTime, toPoint.jobStartTimeRange.latest);
    toPoint.finishedJobTime = addMinutes(toPoint.startedJobTime, toPoint.timeSpentDoingJob);
    if (index + 1 < route.length && toPoint.id === route[index + 1].id) {
      toPoint.timeToExit = 0;
    }
    toPoint.leaveTime = addMinutes(toPoint.finishedJobTime, toPoint.timeToExit);
  }
  return route;
}
function computeScore(mealProposals, distances) {
  if (mealProposals.length === 1) {
    return 0;
  }
  const route = getRoute(mealProposals, distances, false);
  return [{
    scorer: getNumberOfTrips,
    weight: -25
  }, {
    scorer: getMaxMadeLateToClientLocation,
    weight: -5
  }, {
    scorer: getMaxMadeLateToPartnerLocation,
    weight: -1
  }].reduce((score, {
    scorer,
    weight
  }) => score + scorer(route, mealProposals, distances) * weight, 0);
}
function getDestinationPointTypeChecker(type) {
  return destinationPoint => destinationPoint.type === type;
}
function findDriverSchedules(mealProposals, distances, numberOfDrivers) {
  let scoreCache = {};
  const [firstMeal, ...restOfMeals] = mealProposals;
  let optimalDriverSchedules = null;
  let optimalScores = Array(numberOfDrivers).fill(Number.MIN_SAFE_INTEGER);
  helper([[firstMeal]], restOfMeals, 0);
  return optimalDriverSchedules.sort((scheduleA, scheduleB) => getScore(scheduleA) - getScore(scheduleB));
  function helper(driverSchedules, mealProposals, mealProposalIndex) {
    if (mealProposalIndex === mealProposals.length && driverSchedules.length === numberOfDrivers) {
      const {
        isOptimal,
        scores
      } = rankSchedule(driverSchedules);
      if (isOptimal) {
        optimalDriverSchedules = driverSchedules.map(mealProposals => [...mealProposals]);
        optimalScores = [...scores];
      }
      return;
    }
    const mealToAssign = mealProposals[mealProposalIndex];
    if (driverSchedules.length < numberOfDrivers) {
      driverSchedules.push([mealToAssign]);
      helper(driverSchedules, mealProposals, mealProposalIndex + 1);
      driverSchedules.pop();
    }
    const remainingMealsCount = mealProposals.length - mealProposalIndex;
    const remainingSchedulesToFill = numberOfDrivers - driverSchedules.length;
    const sortedDriverSchedules = [...driverSchedules].sort((scheduleA, scheduleB) => scheduleA.length - scheduleB.length);
    if (remainingMealsCount > remainingSchedulesToFill) {
      for (let schedule of sortedDriverSchedules) {
        schedule.push(mealToAssign);
        const {
          isOptimal
        } = rankSchedule(sortedDriverSchedules);
        if (isOptimal) {
          helper(sortedDriverSchedules, mealProposals, mealProposalIndex + 1);
        }
        schedule.pop();
      }
    }
  }
  function getScore(mealProposals) {
    const key = mealProposals.map(({
      orderNumber
    }) => orderNumber).sort((orderNumberA, orderNumberB) => orderNumberA - orderNumberB).join('');
    let score = scoreCache[key];
    if (typeof score !== 'undefined') {
      return score;
    }
    score = computeScore(mealProposals, distances);
    scoreCache[key] = score;
    return score;
  }
  function rankSchedule(driverSchedules) {
    const scores = driverSchedules.map(meals => getScore(meals)).sort((scoreA, scoreB) => scoreA - scoreB);
    for (let i = 0; i < scores.length; i++) {
      if (scores[i] < optimalScores[i]) {
        return {
          isOptimal: false
        };
      }
      if (scores[i] > optimalScores[i]) {
        return {
          isOptimal: true,
          scores
        };
      }
    }
    return {
      isOptimal: false
    };
  }
}
function getClientLocationDestinationPoint(mealProposal, withMetadata) {
  let {
    ClientLocation: {
      id,
      fullAddress,
      name,
      streetAddress,
      timeToPark,
      timeToEnter,
      timeToExit,
      timezone,
      Client
    },
    deliveryDate,
    headCount,
    orderNumber
  } = mealProposal;
  const timeSpentSettingUp = headCount <= BASE_HEAD_COUNT ? MIN_TIME_SPENT_SETTING_UP : Math.min(MAX_TIME_SPENT_SETTING_UP, MIN_TIME_SPENT_SETTING_UP + Math.ceil(ADDITIONAL_TIME_SPENT_PICKING_UP_PER_PERSON_ABOVE_BASE_HEAD_COUNT * (headCount - BASE_HEAD_COUNT)));
  deliveryDate = new Date(deliveryDate);
  let clientLocationDestinationPoint = {
    type: 'ClientLocation',
    id,
    orderNumber,
    headCount,
    date: deliveryDate,
    timezone,
    ...getTimeOfDay(deliveryDate, timezone),
    timeToPark,
    timeToEnter,
    timeToExit,
    timeSpentDoingJob: timeSpentSettingUp,
    jobStartTimeRange: {
      earliest: addMinutes(deliveryDate, -(DELIVERY_GRACE_PERIOD + timeSpentSettingUp)),
      latest: addMinutes(deliveryDate, -timeSpentSettingUp)
    }
  };
  if (withMetadata) {
    clientLocationDestinationPoint.mealProposal = mealProposal;
    clientLocationDestinationPoint.parentEntityName = Client.name;
    clientLocationDestinationPoint.abbreviatedName = Client.abbreviatedName;
    clientLocationDestinationPoint.fullAddress = fullAddress;
    clientLocationDestinationPoint.streetAddress = streetAddress;
    clientLocationDestinationPoint.locationName = name;
    clientLocationDestinationPoint.logoUrl = Client.logoUrl;
  }
  return clientLocationDestinationPoint;
}
function getMaxMadeLateToClientLocation(route, mealProposals, distances) {
  let singleDeliveryLateBy = {};
  let lateByDifferences = [];
  mealProposals.map(mealProposal => getRoute([mealProposal], distances, false)).map(route => route[route.length - 1]).forEach(({
    id,
    orderNumber,
    lateBy
  }) => {
    const key = id + orderNumber;
    singleDeliveryLateBy[key] = lateBy;
  });
  route.filter(getDestinationPointTypeChecker('ClientLocation')).forEach(({
    id,
    orderNumber,
    lateBy
  }) => {
    const key = id + orderNumber;
    lateByDifferences.push(lateBy - singleDeliveryLateBy[key]);
  });
  return Math.max(...lateByDifferences);
}
function getMaxMadeLateToPartnerLocation(route, mealProposals, distances) {
  if (route.some(getDestinationPointTypeChecker('PartnerLocation'))) {
    return 0;
  }
  let singleDeliveryLateBy = {};
  let lateByDifferences = [];
  mealProposals.map(mealProposal => getRoute([mealProposal], distances, false)).reduce((destinationPoints, route) => destinationPoints.concat(route), []).filter(getDestinationPointTypeChecker('PartnerLocation')).forEach(({
    id,
    orderNumber,
    lateBy
  }) => {
    const key = id + orderNumber;
    lateByDifferences[key] = lateBy;
  });
  route.filter(getDestinationPointTypeChecker('PartnerLocation')).forEach(({
    id,
    orderNumber,
    lateBy,
    duplicates
  }, currentIndex) => {
    const alreadyPickingUp = duplicates.some(({
      index: indexOfDuplicate
    }) => indexOfDuplicate < currentIndex);
    const key = id + orderNumber;
    if (!alreadyPickingUp) {
      lateByDifferences.push(lateBy - singleDeliveryLateBy[key]);
    }
  });
  return Math.max(...lateByDifferences);
}
function getNumberOfTrips(route) {
  const closeProximityLocationsCount = route.reduce((closeProximityLocationsCount, point) => typeof point.metersToLocation !== 'undefined' && point.metersToLocation < CLOSE_PROXIMITY_DISTANCE ? closeProximityLocationsCount + 1 : closeProximityLocationsCount, 0);
  return route.length - closeProximityLocationsCount;
}
function getPartnerLocationDestinationPoint(mealProposal, purchaseOrder, withMetadata) {
  let {
    PartnerLocation: {
      id,
      fullAddress,
      name,
      streetAddress,
      timeToPark,
      timeToEnter,
      timeToExit,
      timezone,
      Partner
    },
    pickupTime
  } = purchaseOrder;
  let {
    orderNumber,
    headCount
  } = mealProposal;
  const timeSpentPickingUp = headCount <= BASE_HEAD_COUNT ? MIN_TIME_SPENT_PICKING_UP : Math.min(MAX_TIME_SPENT_PICKING_UP, MIN_TIME_SPENT_PICKING_UP + Math.ceil(ADDITIONAL_TIME_SPENT_SETTING_UP_PER_PERSON_ABOVE_BASE_HEAD_COUNT * (headCount - BASE_HEAD_COUNT)));
  pickupTime = new Date(pickupTime);
  const partnerLocationDestinationPoint = {
    type: 'PartnerLocation',
    id,
    orderNumber,
    headCount,
    date: pickupTime,
    timezone,
    ...getTimeOfDay(pickupTime, timezone),
    timeSpentDoingJob: timeSpentPickingUp,
    timeToPark,
    timeToEnter,
    timeToExit,
    jobStartTimeRange: {
      earliest: pickupTime,
      latest: pickupTime
    }
  };
  if (withMetadata) {
    partnerLocationDestinationPoint.mealProposal = mealProposal;
    partnerLocationDestinationPoint.purchaseOrder = purchaseOrder;
    partnerLocationDestinationPoint.parentEntityName = Partner.name;
    partnerLocationDestinationPoint.fullAddress = fullAddress;
    partnerLocationDestinationPoint.streetAddress = streetAddress;
    partnerLocationDestinationPoint.locationName = name;
    partnerLocationDestinationPoint.logoUrl = Partner.logoUrl;
  }
  return partnerLocationDestinationPoint;
}
function getRoute(mealProposals, distances, withMetadata = true) {
  if (!mealProposals || mealProposals.length === 0) {
    return [];
  }
  let destinationPoints = mealProposals.map(mealProposal => getSingleMealDestinationPoints(mealProposal, withMetadata)).reduce((destinationPoints, destinationPointGroup) => destinationPoints.concat(destinationPointGroup), []).sort((pointA, pointB) => {
    const timeDifference = differenceInMinutes(pointA.date, pointB.date);
    if (timeDifference !== 0 && timeDifference !== -0) {
      return timeDifference;
    }
    if (getDestinationPointTypeChecker('PartnerLocation')(pointA) && getDestinationPointTypeChecker('ClientLocation')(pointB)) {
      return 1;
    }
    if (getDestinationPointTypeChecker('ClientLocation')(pointA) && getDestinationPointTypeChecker('PartnerLocation')(pointB)) {
      return -1;
    }
    return pointB.orderNumber - pointA.orderNumber;
  });
  const destinationPointGroups = groupDestinationPoints(destinationPoints);
  let route = sortDestinationPointsWithinGroup(destinationPointGroups, distances).reduce((destinationPoints, destinationPointGroup) => destinationPoints.concat(destinationPointGroup), []);
  route = addReferencesToDuplicateDestinationPoints(route);
  return calculateTimes(route, distances);
}
function getSingleMealDestinationPoints(mealProposal, withMetadata) {
  const clientLocationDestinationPoint = getClientLocationDestinationPoint(mealProposal, withMetadata);
  return [...getClientApproved(mealProposal.MealOptions).PurchaseOrders.filter(({
    PartnerLocation: {
      Partner
    }
  }) => Partner.name !== 'Spork Bytes').map(purchaseOrder => getPartnerLocationDestinationPoint(mealProposal, purchaseOrder, withMetadata)), clientLocationDestinationPoint];
}
function groupDestinationPoints(destinationPoints) {
  const [firstDestinationPoint, ...remainingDestinationPoints] = destinationPoints;
  let destinationPointGroups = [[firstDestinationPoint]];
  let previousDestinationPoint = firstDestinationPoint;
  for (let destinationPoint of remainingDestinationPoints) {
    const timeDifference = Math.abs(differenceInMinutes(destinationPoint.date, previousDestinationPoint.date));
    if (timeDifference === 0 && getDestinationPointTypeChecker(previousDestinationPoint.type)(destinationPoint)) {
      destinationPointGroups[destinationPointGroups.length - 1].push(destinationPoint);
    } else {
      destinationPointGroups.push([destinationPoint]);
    }
    previousDestinationPoint = destinationPoint;
  }
  return destinationPointGroups;
}
function sortDestinationPointsWithinGroup(destinationPointGroups, distances) {
  if (destinationPointGroups.every(destinationPointGroup => destinationPointGroup.length === 1)) {
    return destinationPointGroups;
  }
  destinationPointGroups = [[], ...destinationPointGroups];
  let minDriveTime = Number.MAX_SAFE_INTEGER;
  let path;
  helper(0, [], null, 0, []);
  return path;
  function helper(destinationPointGroupIndex, visitedPointsWithinDestinationPointGroup, currentPoint, currentDriveTime, currentPath) {
    const allPointsInDestinationPointGroupVisited = destinationPointGroups[destinationPointGroupIndex].every(point => point === currentPoint || visitedPointsWithinDestinationPointGroup.includes(point));
    const onLastDestinationPointGroup = destinationPointGroupIndex === destinationPointGroups.length - 1;
    if (onLastDestinationPointGroup && allPointsInDestinationPointGroupVisited) {
      if (currentDriveTime < minDriveTime) {
        minDriveTime = currentDriveTime;
        path = [...currentPath, currentPoint];
      }
      return;
    }
    let unVisitedPoints;
    if (allPointsInDestinationPointGroupVisited) {
      destinationPointGroupIndex++;
      unVisitedPoints = destinationPointGroups[destinationPointGroupIndex];
    } else {
      unVisitedPoints = destinationPointGroups[destinationPointGroupIndex].filter(point => !visitedPointsWithinDestinationPointGroup.includes(point) && point !== currentPoint);
    }
    if (currentPoint) {
      currentPath.push(currentPoint);
      visitedPointsWithinDestinationPointGroup.push(currentPoint);
    }
    for (let nextPoint of unVisitedPoints) {
      const driveTime = !currentPoint ? 0 : distances[currentPoint.id][nextPoint.id][currentPoint.isWeekday][currentPoint.timeOfDay].minutesToLocation;
      helper(destinationPointGroupIndex, allPointsInDestinationPointGroupVisited ? [] : visitedPointsWithinDestinationPointGroup, nextPoint, currentDriveTime + driveTime, currentPath);
    }
    currentPath.pop();
    visitedPointsWithinDestinationPointGroup.pop();
  }
}

export { computeScore, findDriverSchedules, getDestinationPointTypeChecker, getRoute };
